import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import OurStory from "@/pages/OurStory";
import TheProblem from "@/pages/TheProblem";
import OurSolution from "@/pages/OurSolution";
import Technology from "@/pages/Technology";
import ForBusiness from "@/pages/ForBusiness";
import Sustainability from "@/pages/Sustainability";
import Contact from "@/pages/Contact";

const PageShell = ({ children }) => (
  <motion.main
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -16 }}
    transition={{ duration: 0.45, ease: [0.25, 1, 0.5, 1] }}
  >
    {children}
  </motion.main>
);

const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });
  return (
    <motion.div
      data-testid="scroll-progress-bar"
      className="fixed top-0 left-0 right-0 h-[3px] bg-emerald origin-left z-[70]"
      style={{ scaleX }}
    />
  );
};

function AppContent() {
  const location = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [location.pathname]);

  return (
    <div className="App bg-cream min-h-screen">
      <ScrollProgress />
      <Navbar />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageShell><Home /></PageShell>} />
          <Route path="/our-story" element={<PageShell><OurStory /></PageShell>} />
          <Route path="/the-problem" element={<PageShell><TheProblem /></PageShell>} />
          <Route path="/our-solution" element={<PageShell><OurSolution /></PageShell>} />
          <Route path="/technology" element={<PageShell><Technology /></PageShell>} />
          <Route path="/for-business" element={<PageShell><ForBusiness /></PageShell>} />
          <Route path="/sustainability" element={<PageShell><Sustainability /></PageShell>} />
          <Route path="/contact" element={<PageShell><Contact /></PageShell>} />
          <Route path="*" element={<PageShell><Home /></PageShell>} />
        </Routes>
      </AnimatePresence>
      <Footer />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
